import { Check, Download, Share } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useT } from '../lib/i18n'
import {
  canPrompt,
  detectPlatform,
  installedDuringThisVisit,
  isInstalled,
  onAvailabilityChange,
  promptInstall,
  type Platform,
} from '../lib/install'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { EmptyState } from '../ui/EmptyState'

/**
 * `/install` — the distribution channel.
 *
 * Suap has no store listing, so this page is the funnel. It has to be honest
 * about a platform split it cannot paper over: Chromium can show a real install
 * dialog, iOS Safari has no install API at all, and a desktop browser mostly
 * cannot install a phone-scope app in a way anyone wants.
 *
 * **No button that does nothing.** Where an install cannot be triggered, the page
 * shows the steps or explains why, rather than rendering a control that looks
 * like the Chromium one and silently fails. That is the acceptance criterion and
 * it is also the difference between a funnel and a dead end.
 *
 * SUAP-0106.
 */
export function Install() {
  const { t } = useT()
  const [platform] = useState<Platform>(() => detectPlatform())
  const [installed, setInstalled] = useState(() => isInstalled() || installedDuringThisVisit())
  const [promptable, setPromptable] = useState(() => canPrompt())
  const [dismissed, setDismissed] = useState(false)

  useEffect(
    () =>
      onAvailabilityChange(() => {
        const available = canPrompt()
        setPromptable(available)
        // `install.ts` notifies this same channel on `appinstalled`, and this is
        // the line that reads it. `installed` used to be read once, at mount, so
        // the page could not learn about an install it had not performed itself:
        // somebody who followed the dismissal message into the browser menu came
        // back to a page still telling them to open the browser menu. SUAP-0116.
        //
        // `isInstalled()` is re-read alongside it and is *not* redundant — it is
        // what answers in the installed app's own window, where no
        // `appinstalled` fired because the install predates the page.
        const done = isInstalled() || installedDuringThisVisit()
        if (done) setInstalled(true)
        // A fresh event means the browser is offering again, so the "no offer
        // right now, use the menu" message is stale. Without this, a re-fired
        // prompt renders the button and the message that says there is no
        // button, together. Found in review (SUAP-0114). A completed install
        // makes it stale for the more obvious reason.
        if (available || done) setDismissed(false)
      }),
    [],
  )

  async function install() {
    const outcome = await promptInstall()
    if (outcome === 'accepted') setInstalled(true)
    if (outcome === 'dismissed') setDismissed(true)
  }

  if (installed) {
    return (
      <EmptyState emoji="🎉" title={t('install.title')} body={t('install.installed')} />
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-base text-ink">{t('install.why')}</p>

      {promptable ? (
        <Button variant="accent" size="lg" block onClick={install} icon={<Download aria-hidden size={20} />}>
          {t('install.action')}
        </Button>
      ) : null}

      {/* Outside the `promptable` branch, and that is the whole fix (SUAP-0114).

          The reason is not a race, and calling it one was imprecise: after any
          dismissal `promptInstall()` has set `deferred = null`, so `promptable`
          is **unconditionally** false from then on. No render can ever have both
          flags true, batched or not, and reordering the notification would have
          bought a flash rather than a fix. Nested inside `promptable`, this
          message was unreachable in every run of the app that has existed: the
          user dismissed Chrome's dialog and got the button gone, nothing said,
          and nothing to do — on the one route that is the distribution channel.

          `role="status"` because it appears after a user action, replacing the
          control that had focus. Raised in review. */}
      {dismissed ? (
        <p role="status" className="text-sm text-ink-soft">
          {t('install.dismissed')}
        </p>
      ) : null}

      {platform === 'ios' ? (
        <Card>
          <div className="flex flex-col gap-3">
            <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
              <Share aria-hidden size={18} />
              {t('install.iosTitle')}
            </h2>
            <ol className="flex flex-col gap-2 text-sm text-ink">
              {(['install.iosStep1', 'install.iosStep2', 'install.iosStep3'] as const).map((key, index) => (
                <li key={key} className="flex gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-chip bg-ink text-xs text-ground">
                    {index + 1}
                  </span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-ink-soft">{t('install.iosNote')}</p>
          </div>
        </Card>
      ) : null}

      {platform === 'other' && !promptable ? (
        <Card>
          <p className="text-sm text-ink">{t('install.unsupported')}</p>
        </Card>
      ) : null}

      {/* Shown on every platform: someone reading this on a laptop deserves to
          know the narrow column is a decision, not a broken layout. */}
      <Card>
        <div className="flex flex-col gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
            <Check aria-hidden size={18} />
            {t('install.desktopTitle')}
          </h2>
          <p className="text-sm text-ink-soft">{t('install.desktopBody')}</p>
        </div>
      </Card>
    </div>
  )
}
